import { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Grid, Select, MenuItem, FormControl,
  InputLabel, TextField, Button, CircularProgress, Alert,
  Table, TableHead, TableRow, TableCell, TableBody, Chip,
  Tabs, Tab, IconButton,
} from '@mui/material';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip,
  ResponsiveContainer, ReferenceLine,
} from 'recharts';
import { TrendingUp, Refresh, PlayArrow, History } from '@mui/icons-material';
import {
  runBacktest, listBacktests, getBacktest, BacktestParams, BacktestResult, BacktestSummary,
} from '../services/api';

const STRATEGIES = [
  { value: 'sma_crossover', label: 'SMA Crossover' },
  { value: 'bollinger_bands', label: 'Bollinger Bands' },
];
const INTERVALS = [
  { value: '1h', label: '1H' },
  { value: '4h', label: '4H' },
  { value: '1d', label: '1D' },
];
const SYMBOLS = ['BTCUSDT', 'ETHUSDT', 'SOLUSDT', 'BNBUSDT', 'ADAUSDT'];

function MetricChip({ label, value, color }: { label: string; value: string | number; color?: string }) {
  const isPos = typeof value === 'number' && value >= 0;
  const displayColor = color || (isPos ? '#22c55e' : '#ef4444');
  return (
    <Paper sx={{ p: 1.5, textAlign: 'center', minWidth: 100 }}>
      <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
      <Typography variant="body1" fontWeight={700} sx={{ color: displayColor }}>
        {typeof value === 'number' ? value.toFixed(2) : value}
      </Typography>
    </Paper>
  );
}

export default function BacktestPanel() {
  const [tab, setTab] = useState(0);
  const [symbol, setSymbol] = useState('BTCUSDT');
  const [strategy, setStrategy] = useState('sma_crossover');
  const [interval, setInterval] = useState('1h');
  const [limit, setLimit] = useState(200);
  const [capital, setCapital] = useState(10000);
  const [positionPct, setPositionPct] = useState(100);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<BacktestResult | null>(null);
  const [history, setHistory] = useState<BacktestSummary[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const loadHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const data = await listBacktests();
      setHistory(data);
    } catch (e: any) {
      console.error('History load error', e);
    } finally {
      setLoadingHistory(false);
    }
  }, []);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  const handleRun = async () => {
    setRunning(true);
    setError(null);
    setResult(null);
    try {
      const params: BacktestParams = { symbol, strategy, interval, limit, initial_capital: capital, position_size_pct: positionPct };
      const res = await runBacktest(params);
      setResult(res);
      setTab(1);
      loadHistory();
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Backtest failed');
    } finally {
      setRunning(false);
    }
  };

  const handleSelectHistory = async (id: number) => {
    try {
      const res = await getBacktest(id);
      setResult(res);
      setTab(1);
    } catch (e: any) {
      setError(e?.message || 'Failed to load backtest');
    }
  };

  // Build equity curve chart data
  const equityData = result?.equity_curve?.map((val, i) => ({
    t: i,
    equity: val,
    initial: result.initial_capital,
  })) || [];

  return (
    <Box>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Tab label="New Backtest" icon={<PlayArrow />} iconPosition="start" />
        <Tab label="Results" icon={<TrendingUp />} iconPosition="start" disabled={!result} />
        <Tab label="History" icon={<History />} iconPosition="start" onClick={loadHistory} />
      </Tabs>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {tab === 0 && (
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Symbol</InputLabel>
              <Select value={symbol} label="Symbol" onChange={e => setSymbol(e.target.value)}>
                {SYMBOLS.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Strategy</InputLabel>
              <Select value={strategy} label="Strategy" onChange={e => setStrategy(e.target.value)}>
                {STRATEGIES.map(s => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Interval</InputLabel>
              <Select value={interval} label="Interval" onChange={e => setInterval(e.target.value)}>
                {INTERVALS.map(i => <MenuItem key={i.value} value={i.value}>{i.label}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField label="Candles" type="number" value={limit} size="small" fullWidth
              inputProps={{ min: 50, max: 2000 }}
              onChange={e => setLimit(Math.max(50, Math.min(2000, Number(e.target.value))))} />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField label="Capital ($)" type="number" value={capital} size="small" fullWidth
              onChange={e => setCapital(Number(e.target.value))} />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField label="Position Size (%)" type="number" value={positionPct} size="small" fullWidth
              inputProps={{ min: 1, max: 100 }}
              onChange={e => setPositionPct(Math.max(1, Math.min(100, Number(e.target.value))))} />
          </Grid>
          <Grid item xs={12}>
            <Button variant="contained" startIcon={running ? <CircularProgress size={16} color="inherit" /> : <PlayArrow />}
              onClick={handleRun} disabled={running} size="large">
              {running ? 'Running Backtest...' : 'Run Backtest'}
            </Button>
          </Grid>
        </Grid>
      )}

      {tab === 1 && result && (
        <Box>
          {/* Metrics row */}
          <Grid container spacing={1.5} sx={{ mb: 2 }}>
            <Grid item><MetricChip label="Return" value={`${result.total_return >= 0 ? '+' : ''}${result.total_return_pct.toFixed(2)}%`} /></Grid>
            <Grid item><MetricChip label="Final Equity" value={`$${result.final_equity.toFixed(2)}`} /></Grid>
            <Grid item><MetricChip label="Win Rate" value={`${result.win_rate.toFixed(0)}%`} /></Grid>
            <Grid item><MetricChip label="Sharpe" value={result.sharpe_ratio} /></Grid>
            <Grid item><MetricChip label="Max DD" value={`-${result.max_drawdown_pct.toFixed(2)}%`} color="#ef4444" /></Grid>
            <Grid item><MetricChip label="Trades" value={result.total_trades} /></Grid>
            <Grid item><MetricChip label="Won" value={result.winning_trades} color="#22c55e" /></Grid>
            <Grid item><MetricChip label="Lost" value={result.losing_trades} color="#ef4444" /></Grid>
            <Grid item><MetricChip label="Avg Win" value={`$${result.avg_win.toFixed(2)}`} color="#22c55e" /></Grid>
            <Grid item><MetricChip label="Avg Loss" value={`$${result.avg_loss.toFixed(2)}`} color="#ef4444" /></Grid>
          </Grid>

          {/* Equity Curve */}
          {equityData.length > 0 && (
            <Paper sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>Equity Curve — {result.symbol} {result.interval}</Typography>
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={equityData} margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                  <XAxis dataKey="t" tick={false} />
                  <YAxis domain={['auto', 'auto']} tickFormatter={v => `$${v.toFixed(0)}`} width={70} />
                  <RechartsTooltip formatter={(val: number) => [`$${val.toFixed(2)}`, 'Equity']} />
                  <ReferenceLine y={result.initial_capital} stroke="#888" strokeDasharray="3 3" label="Initial" />
                  <Line type="monotone" dataKey="equity" stroke="#22c55e" dot={false} strokeWidth={1.5} />
                </LineChart>
              </ResponsiveContainer>
            </Paper>
          )}

          {/* Trade Log */}
          {result.trades?.length > 0 && (
            <Paper>
              <Typography variant="subtitle2" sx={{ p: 1.5, borderBottom: 1, borderColor: 'divider' }}>
                Trade Log ({result.trades.length} trades)
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>Side</TableCell>
                    <TableCell align="right">Entry Price</TableCell>
                    <TableCell align="right">Exit Price</TableCell>
                    <TableCell align="right">Qty</TableCell>
                    <TableCell align="right">P&L</TableCell>
                    <TableCell align="right">P&L %</TableCell>
                    <TableCell>Exit Reason</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {result.trades.map((t) => (
                    <TableRow key={t.trade_num} sx={{ '&:last-child td': { border: 0 } }}>
                      <TableCell>{t.trade_num}</TableCell>
                      <TableCell>
                        <Chip label={t.side} size="small"
                          sx={{ bgcolor: t.side === 'BUY' ? '#22c55e' : '#ef4444', color: '#fff', fontSize: '0.7rem' }} />
                      </TableCell>
                      <TableCell align="right">{t.entry_price?.toFixed(2)}</TableCell>
                      <TableCell align="right">{t.exit_price?.toFixed(2)}</TableCell>
                      <TableCell align="right">{t.quantity?.toFixed(4)}</TableCell>
                      <TableCell align="right" sx={{ color: t.pnl >= 0 ? '#22c55e' : '#ef4444', fontWeight: 700 }}>
                        {t.pnl >= 0 ? '+' : ''}{t.pnl?.toFixed(2)}
                      </TableCell>
                      <TableCell align="right" sx={{ color: t.pnl_pct >= 0 ? '#22c55e' : '#ef4444' }}>
                        {t.pnl_pct >= 0 ? '+' : ''}{t.pnl_pct?.toFixed(2)}%
                      </TableCell>
                      <TableCell>{t.exit_reason}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          )}
        </Box>
      )}

      {tab === 2 && (
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
            <Typography variant="h6">Backtest History</Typography>
            <IconButton size="small" onClick={loadHistory}><Refresh /></IconButton>
          </Box>
          {loadingHistory ? (
            <CircularProgress size={24} />
          ) : history.length === 0 ? (
            <Typography color="text.secondary">No backtests run yet.</Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Strategy</TableCell>
                  <TableCell>Symbol</TableCell>
                  <TableCell>Interval</TableCell>
                  <TableCell align="right">Return</TableCell>
                  <TableCell align="right">Sharpe</TableCell>
                  <TableCell align="right">Max DD</TableCell>
                  <TableCell align="right">Trades</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {history.map((h) => (
                  <TableRow key={h.id} hover sx={{ cursor: 'pointer' }}>
                    <TableCell>{h.id}</TableCell>
                    <TableCell>{h.strategy_name}</TableCell>
                    <TableCell>{h.symbol}</TableCell>
                    <TableCell>{h.interval}</TableCell>
                    <TableCell align="right" sx={{ color: h.total_return_pct >= 0 ? '#22c55e' : '#ef4444', fontWeight: 700 }}>
                      {h.total_return_pct >= 0 ? '+' : ''}{h.total_return_pct.toFixed(2)}%
                    </TableCell>
                    <TableCell align="right">{h.sharpe_ratio.toFixed(2)}</TableCell>
                    <TableCell align="right" sx={{ color: '#ef4444' }}>-{h.max_drawdown_pct.toFixed(2)}%</TableCell>
                    <TableCell align="right">{h.total_trades}</TableCell>
                    <TableCell>{new Date(h.created_at).toLocaleDateString()}</TableCell>
                    <TableCell>
                      <Button size="small" variant="outlined" onClick={() => handleSelectHistory(h.id)}>View</Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Box>
      )}
    </Box>
  );
}
